import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { paymentService } from '@/services/paymentService';
import { StudentPayment } from '@/types';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const PendingPayments: React.FC = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<StudentPayment[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPayments = async () => {
    try {
      const pendingPayments = await paymentService.getPendingCashPayments();
      setPayments(pendingPayments);
    } catch (error) {
      console.error('Error loading pending payments:', error);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, []);

  const handleMarkAsPaid = async (paymentId: string) => {
    if (!user) return;

    try {
      await paymentService.markCashPaymentAsPaid(
        paymentId,
        user.id,
        `${user.firstName} ${user.lastName}`
      );
      toast.success('Payment marked as paid');
      loadPayments(); // Reload the list
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      toast.error('Failed to mark payment as paid');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (payments.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            No pending cash payments
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Cash Payments</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="space-y-1">
                <p className="font-medium">{payment.studentName}</p>
                <p className="text-sm text-muted-foreground">
                  {payment.courseName}
                </p>
                <p className="text-sm text-muted-foreground">
                  Amount: ${payment.amount}
                </p>
                <p className="text-sm text-muted-foreground">
                  Created: {format(payment.createdAt, 'PPp')}
                </p>
              </div>
              <Button
                onClick={() => handleMarkAsPaid(payment.id)}
                variant="default"
              >
                Mark as Paid
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}; 